import React from "react";

import {
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { fade, makeStyles, useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";

import ContactAutocomplete from "./ContactAutocomplete";

const useStyles = makeStyles((theme) => ({
  popoverPaper: {
    width: 420,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 160px)",
    borderRadius: 10,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 20px 48px rgba(0, 0, 0, 0.42)"
        : "0 20px 48px rgba(15, 23, 42, 0.18)",
  },
  mobilePaper: {
    backgroundColor: theme.palette.background.paper,
  },
  root: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 160px)",
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("xs")]: {
      height: "100%",
      maxHeight: "none",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  title: {
    fontWeight: 600,
  },
  closeButton: {
    padding: theme.spacing(0.75),
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  field: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderColor: fade(theme.palette.divider, 0.92),
      },
      "&:hover fieldset": {
        borderColor: fade(theme.palette.primary.main, 0.4),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
  },
  dateRange: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  actionButton: {
    borderRadius: 10,
  },
}));

const CrmFilterPopover = ({
  open,
  anchorEl,
  filters,
  users,
  contactOptions,
  contactsLoading,
  onChange,
  onContactSearch,
  onClear,
  onApply,
  onClose,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const draftFilterCount = [
    filters.status,
    filters.userId,
    filters.contact,
    filters.dateStart,
    filters.dateEnd,
  ].filter(Boolean).length;

  const content = (
    <Box
      id="crm-advanced-filters"
      className={classes.root}
      role="form"
      aria-label="Filtros avancados do CRM"
    >
      <Box className={classes.header}>
        <Box>
          <Typography id="crm-filters-title" className={classes.title}>
            Filtros
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Ajuste os critérios e aplique quando estiver pronto.
          </Typography>
        </Box>
        <IconButton
          className={classes.closeButton}
          aria-label="Fechar filtros"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.body}>
        <TextField
          className={classes.field}
          select
          fullWidth
          variant="outlined"
          size="small"
          label="Status"
          value={filters.status}
          onChange={(event) => onChange("status", event.target.value)}
          SelectProps={{ inputProps: { "aria-label": "Status" } }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="open">Abertos</MenuItem>
          <MenuItem value="won">Ganhos</MenuItem>
          <MenuItem value="lost">Perdidos</MenuItem>
        </TextField>

        <TextField
          className={classes.field}
          select
          fullWidth
          variant="outlined"
          size="small"
          label="Responsavel"
          value={filters.userId}
          onChange={(event) => onChange("userId", event.target.value)}
          SelectProps={{ inputProps: { "aria-label": "Responsavel" } }}
        >
          <MenuItem value="">Todos</MenuItem>
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.name}
            </MenuItem>
          ))}
        </TextField>

        <ContactAutocomplete
          label="Contato"
          value={filters.contact}
          options={contactOptions}
          loading={contactsLoading}
          className={classes.field}
          onInputChange={onContactSearch}
          onChange={(contact) => onChange("contact", contact)}
        />

        <Box className={classes.dateRange}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            type="date"
            label="Criado a partir de"
            value={filters.dateStart}
            onChange={(event) => onChange("dateStart", event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: filters.dateEnd || undefined,
              "aria-label": "Data inicial de criacao",
            }}
          />
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            type="date"
            label="Criado ate"
            value={filters.dateEnd}
            onChange={(event) => onChange("dateEnd", event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: filters.dateStart || undefined,
              "aria-label": "Data final de criacao",
            }}
          />
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Button
          className={classes.actionButton}
          onClick={onClear}
          disabled={draftFilterCount === 0}
        >
          Limpar
        </Button>
        <Button
          className={classes.actionButton}
          variant="contained"
          color="primary"
          onClick={onApply}
        >
          Aplicar filtros
        </Button>
      </Box>
    </Box>
  );

  if (mobile) {
    return (
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        aria-labelledby="crm-filters-title"
        classes={{ paper: classes.mobilePaper }}
      >
        {content}
      </Dialog>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      classes={{ paper: classes.popoverPaper }}
      PaperProps={{ role: "dialog", "aria-labelledby": "crm-filters-title" }}
    >
      {content}
    </Popover>
  );
};

export default CrmFilterPopover;
